import {
  ObstacleLocation,
  annotateObstacleLocation,
  jsonToObstacleLocation,
  obstacleLocationsToPoints,
} from './obstacles';
import { Units, displayShortUnits } from './dimensions';
import { prepareDate, uuid } from './helpers';

import { Bounds } from 'ngx-img-cropper';
import { Origin } from './dimensions';
import { User } from './user';
import { cloneDeep } from 'lodash-es';

// SAS Could be changed to a provider and pull from server/cache locally
export interface Organization {
  long_name: string;
  name: string;
  url: string;
}

export const MapOrganizations: Organization[] = [
  {
    long_name: 'Agility Association of Canada',
    name: 'AAC',
    url: 'http://www.aac.ca/',
  },
  {
    long_name: 'American Kennel Club',
    name: 'AKC',
    url: 'http://www.akc.org/',
  },
  {
    long_name: 'Australian Shepherd Club of America',
    name: 'ASCA',
    url: 'http://www.asca.org/',
  },
  { long_name: 'Canadian Kennel Club', name: 'CKC', url: 'http://www.ckc.ca/' },
  {
    long_name: 'Cavalier King Charles Spaniel Club',
    name: 'CKCSC',
    url: 'http://www.ckcsc.org//',
  },
  {
    long_name: 'Canine Performance Events',
    name: 'CPE',
    url: 'http://www.k9cpe.com/',
  },
  {
    long_name: 'Dogs On Course in North America',
    name: 'DOCNA',
    url: 'http://www.docna.com/',
  },
  {
    long_name: 'Fédération Cynologique Internationale',
    name: 'FCI',
    url: 'http://www.fci.be',
  },
  {
    long_name: 'North American Dog Agility Council',
    name: 'NADAC',
    url: 'http://www.nadac.com/',
  },
  {
    long_name: 'Sweepstakes Agility',
    name: 'SSA',
    url: 'http://www.sweepstakesagility.com/',
  },
  {
    long_name: 'Teacup Dogs Agility Association',
    name: 'TDAA',
    url: 'http://www.k9tdaa.com/',
  },
  {
    long_name: 'United Kennel Club',
    name: 'UKC',
    url: 'http://www.ukcdogs.com/',
  },
  {
    long_name: 'UK Agility International',
    name: 'UKI',
    url: 'http://www.ukagilityinternational.com/',
  },
  {
    long_name: 'United States Dog Agility Association',
    name: 'USDAA',
    url: 'http://www.usdaa.com/',
  },
  {
    long_name: 'Virtual Agility League',
    name: 'VALOR',
    url: 'http://www.dogtrainingraleighnc.net/VirtualAgilityLeague/VALOR_home.html',
  },
  {
    long_name: 'World Agility Open',
    name: 'WAO',
    url: 'http://www.worldagilityopen.com/',
  },
];

export const MapLevels: string[] = [
  '1',
  '2',
  '3',
  '4',
  '5',
  'A',
  'Advanced',
  'Agility',
  'B',
  'Beginner',
  'Champion',
  'Elementary',
  'Elite',
  'Excellent',
  'Expert',
  'Fun',
  'Intermediate',
  'Intern',
  'International',
  'Intro',
  'Masters Challenge',
  'Masters',
  'Nationals',
  'Novice',
  'Open',
  'Practice',
  'PI',
  'PII',
  'PIII',
  'Premier',
  'Senior',
  'Specialist',
  'Standard',
  'Starters',
  'Superior',
  'T2B',
  'Tournament',
  'Veterans',
];

export const MapEvents: string[] = [
  'ACT',
  'Agility',
  'Agility I',
  'Agility II',
  'Agility III',
  'Biathlon',
  'Challenge',
  'Chances',
  'Colors',
  'Extreme Barrel Racing',
  'Extreme Chances',
  'Extreme Gaters',
  'Extreme Hoopers',
  'FAST',
  'Finals Qualifier',
  'FullHouse',
  'Gamblers',
  'Gaters',
  'Grand Prix',
  'Hoopers',
  'JWW',
  'Jackpot',
  'Jokers Wild',
  'Jumpers',
  'Jumping',
  'Knockout',
  'Masters',
  'Nationals',
  'Nursery',
  'Pairs',
  'Perfect 50',
  'Petit Prix',
  'Power and Speed',
  'Regular',
  'Seminar',
  'Snakes N Ladders',
  'Snooker',
  'Speed Stakes',
  'Standard',
  'Steeplechase',
  'T2B',
  'Team Relay',
  'Team',
  'Time Gamble',
  'TouchNGo',
  'Trigility',
  'Tunnelers',
  'Weavers',
  'Wildcard',
];

export const MapHeightGroups = [
  'Large',
  'Medium',
  'Mini',
  'Midi',
  'Maxi',
  'Small',
];

export class Map {
  id: string;
  name: string;
  organization: string;
  host: string;
  location: string;
  designers: string[];
  judges: string[];
  notes: string;
  tags: string[];
  units: Units;
  width: number;
  height: number;
  created: string;
  updated: string;
  performed: string;
  distance: number;
  sct: number;
  level: string; // Masters, Novice, Excellent
  event: string; // jumping, standard, gamblers, steeplechase
  height_group: string; // mini, midi, maxi
  url: string;
  cropped_image: string;
  original_image: string;
  // these are derived fields and ignored by the server if sent:
  croppedImageURL: string;
  originalImageURL: string;
  croppingBounds: Bounds;
  width_px: number;
  height_px: number;
  series: string;
  series_image: string;
  seriesImageURL: string;
  origin: Origin; // display origin
  obstacle_locations: ObstacleLocation[];
  gridspacing: number;

  constructor({
    id = null,
    name = '',
    organization = '',
    host = '',
    location = '',
    designers = [],
    judges = [],
    notes = '',
    tags = [],
    units = Units.feet,
    width = null,
    height = null,
    created = '',
    updated = '',
    performed = '',
    distance = null,
    sct = null,
    level = '',
    event = '',
    height_group = '',
    url = '',
    cropped_image = '',
    original_image = '',
    croppedImageURL = '',
    originalImageURL = '',
    croppingBounds = null,
    width_px = 0,
    height_px = 0,
    series = null,
    series_image = '',
    seriesImageURL = null,
    origin = null, // needs to be set explicitly
    obstacle_locations = [],
    gridspacing = 10,
    ...rest
  }: {
    id?: string;
    name?: string;
    organization?: string;
    host?: string;
    location?: string;
    designers?: string[];
    judges?: string[];
    notes?: string;
    tags?: string[];
    units?: Units;
    width?: number;
    height?: number;
    created?: string;
    updated?: string;
    performed?: string;
    distance?: number;
    sct?: number;
    level?: string;
    event?: string;
    height_group?: string;
    url?: string;
    cropped_image?: string;
    original_image?: string;
    croppedImageURL?: string;
    originalImageURL?: string;
    croppingBounds?: Bounds;
    width_px?: number;
    height_px?: number;
    series?: string;
    series_image?: string;
    seriesImageURL?: string;
    origin?: Origin;
    obstacle_locations?: ObstacleLocation[];
    gridspacing?: number;
  }) {
    const now = new Date();
    this.id = id ? id : uuid();
    this.name = name;
    this.organization = organization;
    this.host = host;
    this.location = location;
    this.designers = designers;
    this.judges = judges;
    this.notes = notes;
    this.tags = tags;
    this.units = units;
    this.width = width;
    this.height = height;
    this.created = prepareDate(created, now);
    this.updated = prepareDate(updated, now);
    this.performed = prepareDate(performed, null);
    this.distance = distance;
    this.sct = sct;
    this.level = level;
    this.event = event;
    this.height_group = height_group;
    this.url = url;
    this.cropped_image = cropped_image;
    this.original_image = original_image;
    this.croppedImageURL = croppedImageURL;
    this.originalImageURL = originalImageURL;
    this.croppingBounds = croppingBounds;
    this.width_px = width_px;
    this.height_px = height_px;
    this.series = series;
    this.series_image = series_image;
    this.seriesImageURL = seriesImageURL;
    this.obstacle_locations = obstacle_locations || []; //override undefined
    this.origin = origin;
    this.gridspacing = gridspacing;
    Object.assign(this, rest); // allow old source to edit/persist properties from future versions
  }

  static clone(map: Map): Map {
    const clone = cloneDeep(map);
    clone.id = null;
    clone._rev = null;
    clone.created = null;
    clone.updated = null;
    const newMap = new Map(clone);
    return newMap;
  }

  urlId(): string {
    return this.id;
  }

  displaySCT(): string {
    return `${this.sct} s`;
  }

  displayDistance(): string {
    return `${this.distance} ${this.displayUnits()}`;
  }

  displayUnits(): string {
    return displayShortUnits(this.units);
  }

  designersString(): string {
    return this.designers.join(' ');
  }

  judgesString(): string {
    return this.judges.join(' ');
  }

  tagsString(): string {
    return this.tags.join(' ');
  }

  displayDimensionsFeet(): string {
    if (this.units === Units.feet) {
      return `${this.width} ft x ${this.height} ft`;
    }
    // convert meters to feet
    const width = Math.round(this.width * 3.28084);
    const height = Math.round(this.height * 3.28084);
    return `${width} ft x ${height} ft`;
  }

  displayDimensionsMeters(): string {
    if (this.units === Units.meters) {
      return `${this.width} m x ${this.height} m`;
    }
    // convert feet to meters
    const width = Math.round(this.width * 0.3048);
    const height = Math.round(this.height * 0.3048);
    return `${width} m x ${height} m`;
  }

  createdDateString(): string {
    return new Date(this.created).toUTCString();
  }

  searchString(): string {
    const search = [
      this.name,
      this.organization,
      this.host,
      this.location,
      this.notes,
      this.tagsString(),
      this.designersString(),
      this.judgesString(),
      this.level,
      this.event,
      this.height_group,
      this.displayDimensionsFeet(),
      this.displayDimensionsMeters(),
      this.createdDateString(),
    ]
      .join(' ')
      .toLowerCase();
    return search;
  }

  favorite(user: User): boolean {
    return (
      user && user.settings && user.settings.mapFavorites.indexOf(this.id) > -1
    );
  }

  widthToHeightRatio(): number {
    return this.width_px / this.height_px;
  }

  isSaved(): boolean {
    return Boolean(this.name);
  }

  points(): PointObjectNotation[] {
    return obstacleLocationsToPoints(this.obstacleLocations(), false);
  }

  obstacleLocations(): ObstacleLocation[] {
    return this.obstacle_locations
      .map((o) => jsonToObstacleLocation(o, this.units))
      .filter((loc) => loc !== null);
  }

  annotatedObstacleLocations() {
    return this.obstacleLocations().map((o) => annotateObstacleLocation(o));
  }

  isMetric(): boolean {
    return this.units === Units.meters;
  }
}
